import { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import { doc, onSnapshot } from "firebase/firestore";
import { initMercadoPago, CardPayment } from "@mercadopago/sdk-react";
import { clienteAuth, clienteDb } from "../../utils/clienteAuth";
import { toast } from "../../hooks/useToast";
import { fmtMoeda as fmt } from "../../utils/format";
import { pagarPedidoLoja, verificarPagamentoLoja, novoRequestId, mensagemMP } from "../../utils/mercadoPago";
import { useVerificacaoAutomatica } from "../../hooks/useVerificacaoAutomatica";

/*
 * Pagamento do cliente final pela conta Mercado Pago da LOJA.
 *  - Pix: o backend cria a cobrança; aqui só aparece o QR (dado público).
 *  - Cartão: Card Payment Brick oficial. Número e CVV são digitados no
 *    formulário do Mercado Pago e viram um token de uso único; o backend
 *    recebe só o token, a bandeira, as parcelas e o documento do pagador.
 * Esta tela NUNCA decide que o pedido foi pago: ela acompanha o pedido, que só
 * o servidor marca como "pago" depois de confirmar com o Mercado Pago.
 */

const btnSec = { padding:"12px", background:"transparent", color:"#b2c5d5", border:"1px solid #ffffff26", borderRadius:12, fontWeight:700, fontSize:14, cursor:"pointer" };

// Device ID que o SDK do Mercado Pago gera neste navegador (antifraude e
// aprovação). Não é credencial; o servidor confere o formato antes de repassar.
const dispositivoMP = () => (typeof window.MP_DEVICE_SESSION_ID === "string" ? window.MP_DEVICE_SESSION_ID : undefined);

function usePedidoPago(vendedorId, pedidoId) {
  const [pago, setPago] = useState(false);
  useEffect(() => {
    if (!vendedorId || !pedidoId) return undefined;
    return onSnapshot(doc(clienteDb, "users", vendedorId, "pedidos_online", pedidoId),
      snap => setPago(snap.data()?.status === "pago"), () => {});
  }, [vendedorId, pedidoId]);
  return pago;
}

function PixLoja({ vendedorId, pedidoId, onPago, onOcupado }) {
  const [cobranca, setCobranca] = useState(null);
  const [qr, setQr] = useState("");
  const [erro, setErro] = useState("");
  const [ocupado, setOcupado] = useState(true);
  // Pago pelo pedido (servidor) ou pela resposta de uma consulta desta janela
  const [confirmadoAqui, setConfirmadoAqui] = useState(false);
  const pago = usePedidoPago(vendedorId, pedidoId) || confirmadoAqui;
  const requestId = useRef(novoRequestId());
  // Enquanto o PIX é gerado/consultado, a janela não fecha (senão o cancelamento correria junto)
  useEffect(() => { onOcupado?.(ocupado); }, [ocupado, onOcupado]);

  useEffect(() => {
    let atual = true;
    pagarPedidoLoja(clienteAuth.app, { vendedorId, pedidoId, metodo: "pix", requestId: requestId.current, deviceId: dispositivoMP() })
      .then(r => { if (atual) setCobranca(r); })
      .catch(e => { if (atual) setErro(mensagemMP(e)); })
      .finally(() => { if (atual) setOcupado(false); });
    return () => { atual = false; };
  }, [vendedorId, pedidoId]);

  useEffect(() => {
    let atual = true;
    if (cobranca?.pix?.codigo) QRCode.toDataURL(cobranca.pix.codigo, { width: 240, margin: 1 }).then(u => { if (atual) setQr(u); }).catch(() => {});
    return () => { atual = false; };
  }, [cobranca]);

  useEffect(() => { if (pago) onPago(); }, [pago, onPago]);
  // Janela aberta: confere sozinha, sem depender do aviso (webhook) do Mercado Pago
  useVerificacaoAutomatica(!!cobranca?.cobrancaId && !pago, async () => {
    const r = await verificarPagamentoLoja(clienteAuth.app, cobranca.cobrancaId);
    if (r?.situacao === "pago") setConfirmadoAqui(true);
  });

  const verificar = async () => {
    setOcupado(true);
    try {
      const r = await verificarPagamentoLoja(clienteAuth.app, cobranca.cobrancaId);
      if (r.situacao === "pago") setConfirmadoAqui(true);
      else toast("Ainda não recebemos a confirmação do Mercado Pago.", "info");
    } catch (e) { toast(mensagemMP(e), "error"); }
    finally { setOcupado(false); }
  };
  const copiar = async () => {
    try { await navigator.clipboard.writeText(cobranca.pix.codigo); toast("Código PIX copiado", "success"); }
    catch { toast("Selecione e copie o código PIX manualmente.", "info"); }
  };

  if (erro) return <p className="store-cart-feedback" role="alert">{erro}</p>;
  if (!cobranca) return <p className="store-checkout-note" role="status">Gerando o PIX…</p>;
  return (
    <>
      <div className="store-checkout-pix">
        {qr ? <img src={qr} alt="QR Code PIX do pedido" /> : <p className="store-checkout-note">Gerando QR Code…</p>}
        <strong>{fmt(cobranca.valor)}</strong>
        <p className="store-checkout-note">Escaneie ou copie o código abaixo. A confirmação é automática.</p>
      </div>
      <div className="store-checkout-copy">
        <input aria-label="Código PIX copia e cola" readOnly value={cobranca.pix?.codigo || ""} onFocus={e => e.target.select()} />
        <button type="button" onClick={copiar} style={btnSec}>Copiar</button>
      </div>
      <p className="store-checkout-note" role="status">⏳ Aguardando o pagamento…</p>
      <button type="button" onClick={verificar} disabled={ocupado} style={{ ...btnSec, width: "100%" }}>{ocupado ? "Consultando…" : "Já paguei — verificar"}</button>
    </>
  );
}

function CartaoLoja({ vendedorId, pedidoId, total, email, publicKey, onPago, onOcupado }) {
  const [erro, setErro] = useState("");
  const pago = usePedidoPago(vendedorId, pedidoId);
  // A chave pública do vendedor é feita para o navegador (não é credencial)
  useMemo(() => { initMercadoPago(publicKey, { locale: "pt-BR" }); }, [publicKey]);
  useEffect(() => { if (pago) onPago(); }, [pago, onPago]);

  const enviar = async formData => {
    setErro("");
    onOcupado?.(true);
    try {
      // Token de uso único: cada tentativa é uma cobrança nova
      const r = await pagarPedidoLoja(clienteAuth.app, {
        vendedorId, pedidoId, metodo: "cartao", requestId: novoRequestId(),
        token: formData.token, payment_method_id: formData.payment_method_id, installments: formData.installments,
        payer: { identification: formData.payer?.identification },
        deviceId: dispositivoMP(),
      });
      if (r.situacao === "pago") return;
      const msg = r.situacao === "falhou" ? "O pagamento foi recusado. Confira os dados ou use outro cartão." : "O pagamento está em análise pelo Mercado Pago. Você será avisado pela loja.";
      setErro(msg);
      if (r.situacao === "falhou") throw new Error(msg);
    } catch (e) {
      setErro(prev => prev || mensagemMP(e));
      throw e;
    } finally {
      onOcupado?.(false);
    }
  };

  return (
    <>
      <CardPayment
        initialization={{ amount: total, payer: { email } }}
        customization={{ paymentMethods: { maxInstallments: 12, types: { excluded: ["debit_card"] } } }}
        locale="pt-BR"
        onSubmit={enviar}
        onError={() => setErro("Não foi possível carregar o formulário do Mercado Pago.")}
      />
      {erro && <p className="store-cart-feedback" role="alert">{erro}</p>}
      <p className="store-checkout-note">🔒 Os dados do cartão são digitados no formulário do Mercado Pago. A loja não recebe o número nem o código de segurança.</p>
    </>
  );
}

export function PagamentoMercadoPagoLoja({ metodo, ...props }) {
  return metodo === "cartao" ? <CartaoLoja {...props} /> : <PixLoja {...props} />;
}
